import React from 'react';
import { AlertCircle, CheckCircle2, Info, HelpCircle } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string;
  type?: 'danger' | 'success' | 'info' | 'confirm';
  confirmText?: string;
  cancelText?: string;
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  type = 'info', 
  confirmText = 'Confirmar', 
  cancelText = 'Cancelar' 
}) => {
  if (!isOpen) return null;

  const icons = {
    danger: <AlertCircle className="text-red-500" size={32} />,
    success: <CheckCircle2 className="text-emerald-500" size={32} />,
    info: <Info className="text-blue-500" size={32} />,
    confirm: <HelpCircle className="text-amber-500" size={32} />
  };

  const colors = {
    danger: 'bg-red-500/10 text-red-500',
    success: 'bg-emerald-500/10 text-emerald-500',
    info: 'bg-blue-500/10 text-blue-500',
    confirm: 'bg-amber-500/10 text-amber-500'
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#05070a]/90 transition-opacity" onClick={onClose}></div>
      
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl max-w-sm w-full relative z-10 overflow-hidden animate-in fade-in zoom-in duration-200 shadow-2xl">
        <div className="p-8 text-center">
          <div className="flex justify-center mb-5">
            <div className={`p-3.5 rounded-2xl ${colors[type]}`}>
              {icons[type]}
            </div>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-2">{title}</h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{message}</p>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          {onConfirm && (
            <button onClick={(e) => { e.stopPropagation(); onConfirm(); }} className={`flex-1 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] ${type === 'danger' ? 'bg-red-600 hover:bg-red-700' : type === 'success' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {confirmText}
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 rounded-xl font-medium text-sm transition-all active:scale-[0.98] hover:bg-slate-200 dark:hover:bg-slate-600">
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
